import styled, { css } from 'styled-components';
import vars from './vars';

/**
 * Transparent text button/link with a hover-revealed background and border.
 * Used in navbars, sidebars, and inline action triggers across the apps.
 *
 * Polymorphism: defaults to a `<button>`. For anchor usage pass `as="a"` and `href`:
 *
 *   <GhostButton onClick={...}>Action</GhostButton>
 *   <GhostButton as="a" href="https://..." target="_blank">Link</GhostButton>
 *
 * Hover background variants:
 *  - `default` (`vars.backgroundColor`) — neutral white, intended for warm-tinted
 *    surfaces such as the danger-tinted card navbars (most common)
 *  - `bright` (`vars.backgroundColorBright`) — cream tone, intended for
 *    neutral/white surfaces such as login forms or content cards
 *
 * `hoverBorder={false}` opts out of the canonical hover border for a few legacy
 * sites that originally lacked it; new code should leave it on.
 *
 * Site-specific tweaks (font-weight, gap, font-size, media queries, image children)
 * should extend via `styled(GhostButton)` rather than expanding this API.
 */
export const GhostButton = styled.button<{
    hoverBg?: 'default' | 'bright';
    hoverBorder?: boolean;
}>`
    display: inline-flex;
    align-items: center;
    padding: 6px 8px;
    border-radius: 6px;
    border: 1px solid transparent;
    background: transparent;
    color: ${vars.foregroundColor};
    font: inherit;
    text-decoration: none;
    cursor: pointer;

    &:hover {
        background-color: ${(p) => (p.hoverBg === 'bright' ? vars.backgroundColorBright : vars.backgroundColor)};
        ${(p) =>
            p.hoverBorder !== false &&
            css`
                border-color: ${vars.dividerColor};
            `}
    }
`;
